import { GetStaticPropsContext } from 'next';

import { PageCategory } from '~/features/analytics/constants';
import { getAllCaseStudies } from '~/features/case-study/controllers/get-all-case-studies';
import { CaseStudyContent } from '~/features/case-study/models/case-study-content';
import { getAllCaseStudies as getAllCaseStudiesV2 } from '~/features/case-study-v2/controllers/get-all-case-studies';
import { CaseStudyContent as CaseStudyContentV2 } from '~/features/case-study-v2/models/case-study-content';
import { getAllEnterpriseResources } from '~/features/enterprise-resource/controllers/get-all-enterprise-resources';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { EnterpriseResourceCenter } from '~/features/page-template/templates/enterprise-resource-center/components/enterprise-resource-center';
import {
  EnterpriseResourceCenterTemplate,
  enterpriseResourceCenterTemplateQuery,
  enterpriseResourceCenterTemplatesSchema,
} from '~/features/page-template/templates/enterprise-resource-center/model';
import { EnterpriseCenterResource } from '~/features/page-template/templates/enterprise-resource-center/types';
import { convertResourcesToStandardFormat } from '~/features/page-template/templates/enterprise-resource-center/utils';

export default function ResourceCenterPage({
  page,
  enterpriseResourceCenterTemplate,
  allResources,
}: {
  page: Page;
  enterpriseResourceCenterTemplate: EnterpriseResourceCenterTemplate;
  allResources: EnterpriseCenterResource[];
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseResourceCenter} />
      <PageLayout withoutVerticalPadding>
        <EnterpriseResourceCenter
          allResources={allResources}
          enterpriseResourceCenterTemplate={enterpriseResourceCenterTemplate}
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  let [page, enterpriseResourceCenterTemplate, allEnterpriseResources, allCaseStudies, allCaseStudiesV2] =
    await Promise.all([
      getPage('/resource-center'),
      getPageTemplate<EnterpriseResourceCenterTemplate>({
        templateName: 'Resource Center',
        templateQuery: enterpriseResourceCenterTemplateQuery,
        templateZodSchema: enterpriseResourceCenterTemplatesSchema,
      }),
      getAllEnterpriseResources(),
      getAllCaseStudies(),
      getAllCaseStudiesV2(),
    ]);

  let combinedCaseStudies: (CaseStudyContent | CaseStudyContentV2)[] = [...allCaseStudies];

  allCaseStudiesV2.forEach((caseStudyV2) => {
    let foundIndex = combinedCaseStudies.findIndex((combinedCaseStudy) => combinedCaseStudy.slug === caseStudyV2.slug);

    if (foundIndex) {
      combinedCaseStudies.splice(foundIndex, 1);
    }
  });

  combinedCaseStudies = [...combinedCaseStudies, ...allCaseStudiesV2];

  const combinedResources = [...allEnterpriseResources, ...combinedCaseStudies]
    .sort((a, b) => b._updatedAt.localeCompare(a._updatedAt))
    .filter((resource) => !resource.excludeFromResourceCenter);

  const enterpriseCenterResources = convertResourcesToStandardFormat(combinedResources);

  return {
    props: {
      page,
      enterpriseResourceCenterTemplate,
      allResources: enterpriseCenterResources,
    },
  };
}
